import React, { createElement, useMemo, useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { pdf, type DocumentProps } from "@react-pdf/renderer";
import PublishBar from "../components/PublishBar";
import StepsBar from "../components/StepsBar";
import SuccessScreen from "../components/SuccessScreen";
import { useToast } from "../../../../Components/Common/Toast/ToastContext";
import Step1ProjectDrawInfo from "../components/steps/Step1ProjectDrawInfo";
import Step2DrawReview from "../components/steps/Step2DrawReview";
import Step3ProgressPhases from "../components/steps/Step3ProgressPhases";
import Step4FlagsNarrative from "../components/steps/Step4FlagsNarrative";
import Step5LienWaivers from "../components/steps/Step5LienWaivers";
import Step6Photos from "../components/steps/Step6Photos";
import Step7InvoiceRecommendation from "../components/steps/Step7InvoiceRecommendation";
import Step8PreviewPublish from "../components/steps/Step8PreviewPublish";
import useConstructionProgressReport from "../../../../hooks/useConstructionProgressReport";
import Skeleton from "../../../../Components/Common/Skeleton/Skeleton";
import { publishProgressReport } from "../../../../services/constructionReportService";
import ConstructionReportPdf from "../components/ConstructionReportPdf";
import { uploadFileToS3 } from "../../../GcPrecon/GcPreconCommercial/UploadFileToS3";
import useScrollToTop from "hooks/useScrollToTop";

interface LocalDraft {
  invoiceAmount?: string;
  invoiceDate?: string;
  invoiceNumber?: string;
  reportDate?: string;
  servicesRequested?: string;
  recommendation?: string;
  recommendationText?: string;
  signatureDate?: string;
  drawReviewBullets?: string;
  drawAssessmentText?: string;
  phaseNotes?: Record<string, string>;
  scheduleNotes?: string;
  flagNarratives?: Record<string, string>;
  flagIncludes?: Record<string, boolean>;
  extraFlags?: Array<{
    id: string;
    phase?: string;
    itemLabel: string;
    itemName?: string;
    cscEstimation: string;
    gcCscText: string;
    narrative: string;
    isIncluded: boolean;
  }>;
  changeOrders?: string;
  offsiteMaterialsNotes?: string;
  signatureDisclaimer?: string;
  lienWaiverMinAmount?: string;
  lienWaiverDescription?: string;
  lienWaiverNote?: string;
  bankItems?: string;
  attachmentsList?: string;
  payableTo?: string;
  mailTo?: string;
  deliveryMethod?: string;
  lockAfterPublish?: string;
  deliveryCc?: string[];
  photoDocuments?: Array<{
    id: string;
    isIncluded: boolean;
    description: string;
  }>;
}

const stepLabels = [
  "Project and Draw Info",
  "Draw Review",
  "Progress and Phases",
  "Flags and Narrative",
  "Lien Waivers",
  "Photos",
  "Invoice and Recommendation",
  "Preview and Publish",
];

const nextLabels = [
  "Next: Draw Review →",
  "Next: Progress and Phases →",
  "Next: Flags and Narrative →",
  "Next: Lien Waivers →",
  "Next: Photos →",
  "Next: Invoice and Recommendation →",
  "Next: Preview and Publish →",
];

const ConstructionReportAuthoringPage = () => {
  const { projectId = "", drawId = "" } = useParams<{
    projectId: string;
    drawId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentStep = Math.min(
    Math.max(parseInt(searchParams.get("step") ?? "1") || 1, 1),
    stepLabels.length,
  );
  const setCurrentStep = (updater: number | ((prev: number) => number)) => {
    const next = typeof updater === "function" ? updater(currentStep) : updater;
    setSearchParams({ step: String(next) }, { replace: true });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const savedDraft = useMemo<LocalDraft | null>(() => {
    if (!projectId || !drawId) return null;
    try {
      const raw = localStorage.getItem(`cra_draft_${projectId}_${drawId}`);
      return raw ? (JSON.parse(raw) as LocalDraft) : null;
    } catch {
      return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, drawId]);

  const [published, setPublished] = useState(false);
  const [stepSubmitted, setStepSubmitted] = useState<Record<number, boolean>>(
    {},
  );
  const [invoiceAmount, setInvoiceAmount] = useState(
    savedDraft?.invoiceAmount ?? "",
  );
  const [invoiceDate, setInvoiceDate] = useState(savedDraft?.invoiceDate ?? "");
  const [invoiceNumber, setInvoiceNumber] = useState(
    savedDraft?.invoiceNumber ?? "",
  );
  const [reportDate, setReportDate] = useState(savedDraft?.reportDate ?? "");
  const [servicesRequested, setServicesRequested] = useState(
    savedDraft?.servicesRequested ?? "Site Inspection, Draw Review and Report",
  );
  const [recommendation, setRecommendation] = useState(
    savedDraft?.recommendation ?? "approve",
  );
  const [recommendationText, setRecommendationText] = useState(
    savedDraft?.recommendationText ??
      "Pay the Draw as noted in this report upon Lender approval.",
  );
  const [signatureDate, setSignatureDate] = useState(
    savedDraft?.signatureDate ?? "",
  );
  const [drawReviewBullets, setDrawReviewBullets] = useState(
    savedDraft?.drawReviewBullets ?? "",
  );
  const [drawAssessmentText, setDrawAssessmentText] = useState(
    savedDraft?.drawAssessmentText ?? "",
  );
  const [phaseNotes, setPhaseNotes] = useState<Record<string, string>>(
    savedDraft?.phaseNotes ?? {},
  );
  const [scheduleNotes, setScheduleNotes] = useState(
    savedDraft?.scheduleNotes ?? "",
  );
  const [flagNarratives, setFlagNarratives] = useState<Record<string, string>>(
    savedDraft?.flagNarratives ?? {},
  );
  const [flagIncludes, setFlagIncludes] = useState<Record<string, boolean>>(
    savedDraft?.flagIncludes ?? {},
  );
  const [extraFlags, setExtraFlags] = useState<
    Array<{
      id: string;
      phase?: string;
      itemLabel: string;
      itemName?: string;
      cscEstimation: string;
      gcCscText: string;
      narrative: string;
      isIncluded: boolean;
    }>
  >(savedDraft?.extraFlags ?? []);
  const [changeOrders, setChangeOrders] = useState(
    savedDraft?.changeOrders ?? "",
  );
  const [offsiteMaterialsNotes, setOffsiteMaterialsNotes] = useState(
    savedDraft?.offsiteMaterialsNotes ?? "",
  );
  const [signatureDisclaimer, setSignatureDisclaimer] = useState(
    savedDraft?.signatureDisclaimer ?? "",
  );
  const [lienWaiverMinAmount, setLienWaiverMinAmount] = useState(
    savedDraft?.lienWaiverMinAmount ?? "",
  );
  const [lienWaiverDescription, setLienWaiverDescription] = useState(
    savedDraft?.lienWaiverDescription ?? "",
  );
  const [lienWaiverNote, setLienWaiverNote] = useState(
    savedDraft?.lienWaiverNote ?? "",
  );
  const [bankItems, setBankItems] = useState(savedDraft?.bankItems ?? "");
  const [attachmentsList, setAttachmentsList] = useState(
    savedDraft?.attachmentsList ?? "",
  );
  const [payableTo, setPayableTo] = useState(savedDraft?.payableTo ?? "");
  const [mailTo, setMailTo] = useState(savedDraft?.mailTo ?? "");
  const [deliveryMethod, setDeliveryMethod] = useState(
    savedDraft?.deliveryMethod ?? "email_platform",
  );
  const [lockAfterPublish, setLockAfterPublish] = useState(
    savedDraft?.lockAfterPublish ?? "true",
  );
  const [deliveryCc, setDeliveryCc] = useState<string[]>(
    Array.isArray(savedDraft?.deliveryCc)
      ? savedDraft.deliveryCc
      : savedDraft?.deliveryCc
        ? [savedDraft.deliveryCc]
        : [],
  );
  const [photoDocuments, setPhotoDocuments] = useState<
    Array<{ id: string; isIncluded: boolean; description: string }>
  >(savedDraft?.photoDocuments ?? []);
  const showToast = useToast();

  const { data, loading } = useConstructionProgressReport(projectId, drawId);
  const prefill = data?.prefill;
  const report = data?.report;
  const navigate = useNavigate();
  useEffect(() => {
    if (!report) return;
    const toDateInput = (d: string | null | undefined) =>
      d ? d.split("T")[0] : "";
    if (report.invoiceNumber) setInvoiceNumber(report.invoiceNumber);
    if (report.invoiceDate) setInvoiceDate(toDateInput(report.invoiceDate));
    if (report.reportDate) setReportDate(toDateInput(report.reportDate));
    if (report.invoiceAmount?.amount != null)
      setInvoiceAmount(String(report.invoiceAmount.amount));
    if (report.servicesRequested)
      setServicesRequested(report.servicesRequested);
    if (report.drawAssessmentText)
      setDrawAssessmentText(report.drawAssessmentText);
    if (report.drawBulletPoints) setDrawReviewBullets(report.drawBulletPoints);
    if (report.changeOrderNotes) setChangeOrders(report.changeOrderNotes);
    if (report.offsiteMaterialsNotes)
      setOffsiteMaterialsNotes(report.offsiteMaterialsNotes);
    if (report.signatureDisclaimer)
      setSignatureDisclaimer(report.signatureDisclaimer);
    if (report.lienWaiverMinAmount?.amount != null)
      setLienWaiverMinAmount(String(report.lienWaiverMinAmount.amount));
    if (report.lienWaiverDescription)
      setLienWaiverDescription(report.lienWaiverDescription);
    if (report.lienWaiverNote) setLienWaiverNote(report.lienWaiverNote);
    if (report.bankActionItems) setBankItems(report.bankActionItems);
    if (report.scheduleNotes) setScheduleNotes(report.scheduleNotes);
    if (report.recommendation) setRecommendation(report.recommendation);
    if (report.recommendationText)
      setRecommendationText(report.recommendationText);
    if (report.signatoryDate)
      setSignatureDate(toDateInput(report.signatoryDate));
    if (report.attachmentsList) setAttachmentsList(report.attachmentsList);
    if (report.invoicePayableTo) setPayableTo(report.invoicePayableTo);
    if (report.invoiceMailTo) setMailTo(report.invoiceMailTo);
    if (report.deliveryCc)
      setDeliveryCc(
        Array.isArray(report.deliveryCc)
          ? report.deliveryCc
          : [report.deliveryCc],
      );
    if (report.deliveryMethod) setDeliveryMethod(report.deliveryMethod);
    setLockAfterPublish(report.lockAfterPublish ? "true" : "false");
    if (report.documents?.length) {
      setPhotoDocuments(
        report.documents.map((d) => ({
          id: d.id,
          isIncluded: d.isIncluded,
          description: d.note ?? d.description ?? "",
        })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  useEffect(() => {
    const narrative = prefill?.inspection?.note?.narrative;
    if (narrative) setDrawAssessmentText(narrative);

    // Seed invoice number/amount from the prefill only when nothing has
    // already populated them (a saved report draft, or the local draft
    // used for the initial state, both take precedence).
    const prefillInvoiceNumber = prefill?.invoiceNumber;
    if (prefillInvoiceNumber) {
      setInvoiceNumber((prev) => prev || prefillInvoiceNumber);
    }
    const prefillFeeAmount =
      prefill?.project?.settings?.constructionReportAuthoringFee?.amount;
    if (prefillFeeAmount != null) {
      setInvoiceAmount((prev) => prev || String(prefillFeeAmount));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefill]);

  useEffect(() => {
    if (!projectId || !drawId) return;
    const draft: LocalDraft = {
      invoiceAmount,
      invoiceDate,
      invoiceNumber,
      reportDate,
      servicesRequested,
      recommendation,
      recommendationText,
      signatureDate,
      drawReviewBullets,
      drawAssessmentText,
      phaseNotes,
      scheduleNotes,
      flagNarratives,
      flagIncludes,
      extraFlags,
      changeOrders,
      offsiteMaterialsNotes,
      signatureDisclaimer,
      lienWaiverMinAmount,
      lienWaiverDescription,
      lienWaiverNote,
      bankItems,
      attachmentsList,
      payableTo,
      mailTo,
      deliveryMethod,
      lockAfterPublish,
      deliveryCc,
      photoDocuments,
    };
    localStorage.setItem(
      `cra_draft_${projectId}_${drawId}`,
      JSON.stringify(draft),
    );
  }, [
    invoiceAmount,
    invoiceDate,
    invoiceNumber,
    reportDate,
    servicesRequested,
    recommendation,
    recommendationText,
    signatureDate,
    drawReviewBullets,
    drawAssessmentText,
    phaseNotes,
    scheduleNotes,
    flagNarratives,
    flagIncludes,
    extraFlags,
    changeOrders,
    offsiteMaterialsNotes,
    signatureDisclaimer,
    lienWaiverMinAmount,
    lienWaiverDescription,
    lienWaiverNote,
    bankItems,
    attachmentsList,
    payableTo,
    mailTo,
    deliveryMethod,
    lockAfterPublish,
    deliveryCc,
    photoDocuments,
    projectId,
    drawId,
  ]);

  const effectivePrefill: NonNullable<typeof prefill> | undefined =
    prefill ??
    (report
      ? {
          project:
            report.project ??
            (null as unknown as NonNullable<typeof prefill>["project"]),
          stakeholders:
            report.stakeholders ??
            (null as unknown as NonNullable<typeof prefill>["stakeholders"]),
          draw:
            report.draw ??
            (null as unknown as NonNullable<typeof prefill>["draw"]),
          inspection: null,
          inspectionSnapshot: report.inspectionSnapshot,
          lineItems: [],
          lienWaiversByDraw: report.lienWaiversByDraw,
          documents: report.documents,
          nextReportNumber: report.reportNumber ?? 0,
          invoiceNumber: report.invoiceNumber ?? "",
        }
      : undefined);

  const steps = useMemo(
    () =>
      stepLabels.map((label, index) => ({
        id: `cra-step-${index + 1}`,
        label,
      })),
    [],
  );

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return (
          !!invoiceNumber.trim() &&
          !!invoiceDate.trim() &&
          !!reportDate.trim() &&
          !!servicesRequested.trim()
        );
      case 2:
        return (
          !!drawReviewBullets.replace(/^•\s*/gm, "").trim() &&
          !!bankItems.trim()
        );
      case 3:
        return !!scheduleNotes.replace(/^•\s*/gm, "").trim();
      case 4: {
        const snapshot = effectivePrefill?.inspectionSnapshot;
        const flaggedIds = snapshot?.phases
          ? snapshot.phases.flatMap((p) =>
              p.items.filter((i) => i.isFlagged).map((i) => i.id),
            )
          : (snapshot?.lineItems ?? [])
              .filter((i) => i.isFlagged)
              .map((i) => i.id);
        return flaggedIds.every(
          (id) => (flagNarratives[id] ?? "").trim() !== "",
        );
      }
      case 7: {
        const mailToValid =
          !mailTo.trim() || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mailTo.trim());
        return (
          !!recommendationText.trim() && !!signatureDate.trim() && mailToValid
        );
      }
      default:
        return true;
    }
  };

  const handleNext = () => {
    setStepSubmitted((prev) => ({ ...prev, [currentStep]: true }));
    const hasVisibleCharError = Array.from(
      document.querySelectorAll<HTMLElement>(".field-error"),
    ).some((el) => el.offsetParent !== null);
    if (!isStepValid(currentStep) || hasVisibleCharError) {
      setTimeout(() => {
        const el = Array.from(
          document.querySelectorAll<HTMLElement>(
            ".field-error, .error-input-red, .hasError",
          ),
        ).find((el) => el.offsetParent !== null);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus?.();
        }
      }, 50);
      return;
    }
    setStepSubmitted((prev) => ({ ...prev, [currentStep]: false }));
    setCurrentStep((prev) => Math.min(prev + 1, steps.length));
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const buildPayload = () => {
    const storedUser = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") ?? "{}");
      } catch {
        return {};
      }
    })();

    const stakeholders = effectivePrefill?.stakeholders;
    const lenderName = stakeholders?.lender?.companyName ?? "";
    const lendingOfficer = stakeholders?.lendingOfficer
      ? `${stakeholders.lendingOfficer.firstName ?? ""} ${stakeholders.lendingOfficer.lastName ?? ""}`.trim()
      : "";
    const deliverTo = [lendingOfficer, lenderName].filter(Boolean).join(", ");

    return {
      reportNumber: effectivePrefill?.nextReportNumber ?? null,
      reportDate: reportDate || null,
      invoiceNumber: invoiceNumber || null,
      invoiceAmount: invoiceAmount
        ? { amount: parseFloat(invoiceAmount), currencyCode: "USD" }
        : null,
      invoiceDate: invoiceDate || null,
      drawAssessmentText: drawAssessmentText || null,
      drawBulletPoints: drawReviewBullets || null,
      changeOrderNotes: changeOrders || null,
      offsiteMaterialsNotes: offsiteMaterialsNotes || null,
      bankActionItems: bankItems || null,
      signatureDisclaimer: signatureDisclaimer || null,
      scheduleNotes: scheduleNotes || null,
      lienWaiverDescription: lienWaiverDescription || null,
      lienWaiverMinAmount: lienWaiverMinAmount
        ? { amount: parseFloat(lienWaiverMinAmount), currencyCode: "USD" }
        : null,
      lienWaiverNote: lienWaiverNote || null,
      lienWaiverDeltaText: null,
      recommendation,
      recommendationText: recommendationText || null,
      attachmentsList: attachmentsList || null,
      signatoryName: storedUser?.companyName ?? null,
      signatoryDate: signatureDate || null,
      invoicePayableTo: payableTo || null,
      invoiceMailTo: mailTo || null,
      deliveryTo: deliverTo || null,
      deliveryCc: deliveryCc.length ? deliveryCc : null,
      deliveryMethod: (
        {
          email_platform: ["email", "push"],
          platform: ["push"],
          email: ["email"],
        } as Record<string, string[]>
      )[deliveryMethod] ?? ["email"],
      lockAfterPublish: lockAfterPublish === "true",
      inspectionSnapshot: (() => {
        const snap = effectivePrefill?.inspectionSnapshot;
        if (!snap) return null;

        const parseNoteLines = (text: string): Record<string, string> => {
          const lines = text
            .split("\n")
            .map((l) => l.replace(/^•\s*/, "").trim())
            .filter(Boolean);
          const result: Record<string, string> = {};
          lines.forEach((l, i) => {
            result[String(i + 1)] = l;
          });
          return result;
        };

        const mergeNote = (item: {
          id: string;
          note: Record<string, string>;
        }) => {
          const phaseText = phaseNotes[item.id];
          const numbered = phaseText ? parseNoteLines(phaseText) : {};
          const flagVal = flagNarratives[item.id];
          const note: Record<string, string> = { ...numbered };
          if (flagVal !== undefined) note.flag = flagVal;
          else if (item.note.flag) note.flag = item.note.flag;
          return note;
        };

        if (snap.phases?.length) {
          return {
            ...snap,
            phases: snap.phases.map((phase) => ({
              ...phase,
              items: phase.items.map((item) => {
                const note = mergeNote(item);
                const extra = extraFlags.find((f) => f.id === item.id);
                const isIncludedFlagInReport = extra
                  ? (extra.isIncluded ?? true)
                  : (flagIncludes[item.id] ?? true);
                if (extra)
                  return {
                    ...item,
                    isFlagged: true,
                    isIncludedFlagInReport,
                    note: { ...note, flag: extra.narrative },
                  };
                return { ...item, isIncludedFlagInReport, note };
              }),
            })),
          };
        }
        if (snap.lineItems?.length) {
          return {
            ...snap,
            lineItems: snap.lineItems.map((item) => {
              const note = mergeNote(item);
              const extra = extraFlags.find((f) => f.id === item.id);
              const isIncludedFlagInReport = extra
                ? (extra.isIncluded ?? true)
                : (flagIncludes[item.id] ?? true);
              if (extra)
                return {
                  ...item,
                  isFlagged: true,
                  isIncludedFlagInReport,
                  note: { ...note, flag: extra.narrative },
                };
              return { ...item, isIncludedFlagInReport, note };
            }),
          };
        }
        return snap;
      })(),
      documents: (photoDocuments.length > 0
        ? photoDocuments
        : (effectivePrefill?.documents ?? [])
      ).map((d) => ({
        id: d.id,
        isIncluded: d.isIncluded,
        note: (d as { description?: string }).description ?? null,
      })),
      servicesRequested: servicesRequested || null,
    };
  };

  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const handlePublish = async () => {
    setIsPublishing(true);
    try {
      const storedUser = (() => {
        try {
          return JSON.parse(localStorage.getItem("user") ?? "{}");
        } catch {
          return {};
        }
      })();

      const el = createElement(ConstructionReportPdf, {
        prefill: effectivePrefill ?? undefined,
        invoiceAmount,
        invoiceDate,
        invoiceNumber,
        reportDate,
        recommendation,
        recommendationText,
        signatureDate,
        servicesRequested,
        drawReviewBullets,
        phaseNotes,
        flagNarratives,
        extraFlags,
        scheduleNotes,
        changeOrders,
        bankItems,
        attachmentsList,
        payableTo,
        mailTo,
        deliveryMethod,
        deliveryCc,
        lockAfterPublish,
        sigName: storedUser?.companyName ?? "",
        sigAddress: storedUser?.address ?? "",
        overallCscEstimation:
          effectivePrefill?.inspectionSnapshot?.overallCscEstimation ?? "",
      }) as React.ReactElement<DocumentProps>;

      const blob = await pdf(el).toBlob();
      const projectName = effectivePrefill?.project?.name ?? "report";
      const fileName = `ConstructionReport-${projectName}-${new Date().toISOString().slice(0, 10)}.pdf`;
      const file = new File([blob], fileName, { type: "application/pdf" });
      const s3Result = await uploadFileToS3(file);
      const constructionReportPdf = { key: s3Result.key ?? "", fileName };

      const payload = { ...buildPayload(), constructionReportPdf };
      const { error } = (await publishProgressReport(
        projectId,
        drawId,
        payload,
        true,
      )) as { error: { message?: string } | null };
      if (error) {
        showToast(error.message ?? "Failed to publish report");
        return;
      }
      localStorage.removeItem(`cra_draft_${projectId}_${drawId}`);
      setPublished(true);
      showToast("Report published");
    } catch (e: unknown) {
      showToast(
        e instanceof Error ? e.message : "Failed to generate or upload PDF",
      );
    } finally {
      setIsPublishing(false);
    }
  };

  const handleSaveDraft = async () => {
    setStepSubmitted((prev) => ({ ...prev, [currentStep]: true }));
    const hasVisibleCharError = Array.from(
      document.querySelectorAll<HTMLElement>(".field-error"),
    ).some((el) => el.offsetParent !== null);
    if (!isStepValid(currentStep) || hasVisibleCharError) {
      setTimeout(() => {
        const el = Array.from(
          document.querySelectorAll<HTMLElement>(
            ".field-error, .error-input-red, .hasError",
          ),
        ).find((el) => el.offsetParent !== null);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.focus?.();
        }
      }, 50);
      return;
    }

    setIsSaving(true);
    const payload = buildPayload();
    const { error } = (await publishProgressReport(
      projectId,
      drawId,
      payload,
      false,
    )) as { error: { message?: string } | null };
    setIsSaving(false);
    if (error) {
      showToast(error.message ?? "Failed to save draft");
      return;
    }
    localStorage.removeItem(`cra_draft_${projectId}_${drawId}`);
    showToast("Draft saved");
  };

  const handleRestart = () => {
    setPublished(false);
    setCurrentStep(1);
  };
  const handleDownload = async () => {
    const storedUser = (() => {
      try {
        return JSON.parse(localStorage.getItem("user") ?? "{}");
      } catch {
        return {};
      }
    })();
    const sigName = storedUser?.companyName ?? "";
    const sigAddress = storedUser?.address ?? "";
    const props = {
      prefill: effectivePrefill ?? undefined,
      invoiceAmount,
      invoiceNumber,
      invoiceDate,
      reportDate,
      recommendation,
      recommendationText,
      signatureDate,
      servicesRequested,
      drawReviewBullets,
      phaseNotes,
      flagNarratives,
      extraFlags,
      scheduleNotes,
      changeOrders,
      bankItems,
      attachmentsList,
      sigName,
      sigAddress,
      deliveryCc,
      deliveryMethod,
      lockAfterPublish,
      mailTo,
      payableTo,
      overallCscEstimation:
        effectivePrefill?.inspectionSnapshot?.overallCscEstimation ?? "",
    };
    const el = createElement(
      ConstructionReportPdf,
      props,
    ) as React.ReactElement<import("@react-pdf/renderer").DocumentProps>;
    const blob = await (pdf(el) as any).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ConstructionReport-${effectivePrefill?.project?.name ?? "report"}-${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const scrollRef = React.useRef<HTMLDivElement>(null);
  useScrollToTop(currentStep, { skipFirstRender: false });
  const screens = [
    <Step1ProjectDrawInfo
      key="step1"
      prefill={effectivePrefill}
      invoiceAmount={invoiceAmount}
      onInvoiceAmountChange={setInvoiceAmount}
      invoiceDate={invoiceDate}
      onInvoiceDateChange={setInvoiceDate}
      invoiceNumber={invoiceNumber}
      onInvoiceNumberChange={setInvoiceNumber}
      reportDate={reportDate}
      onReportDateChange={setReportDate}
      servicesRequested={servicesRequested}
      onServicesRequestedChange={setServicesRequested}
      inspectorName={report?.inspectorName ?? ""}
      submitted={stepSubmitted[1] ?? false}
    />,
    <Step2DrawReview
      key="step2"
      prefill={effectivePrefill}
      drawAssessmentText={drawAssessmentText}
      onDrawAssessmentTextChange={setDrawAssessmentText}
      offsiteMaterialsNotes={offsiteMaterialsNotes}
      onOffsiteMaterialsNotesChange={setOffsiteMaterialsNotes}
      signatureDisclaimer={signatureDisclaimer}
      onSignatureDisclaimerChange={setSignatureDisclaimer}
      drawReviewBullets={drawReviewBullets}
      onDrawReviewBulletsChange={setDrawReviewBullets}
      changeOrders={changeOrders}
      onChangeOrdersChange={setChangeOrders}
      bankItems={bankItems}
      onBankItemsChange={setBankItems}
      submitted={stepSubmitted[2] ?? false}
    />,
    <Step3ProgressPhases
      key="step3"
      prefill={effectivePrefill}
      onPhaseNotesChange={setPhaseNotes}
      scheduleNotes={scheduleNotes}
      onScheduleNotesChange={setScheduleNotes}
      submitted={stepSubmitted[3] ?? false}
    />,
    <Step4FlagsNarrative
      key="step4"
      prefill={effectivePrefill}
      initialExtraFlags={extraFlags}
      onExtraFlagsChange={setExtraFlags}
      onFlagNarrativesChange={setFlagNarratives}
      onFlagIncludesChange={setFlagIncludes}
      submitted={stepSubmitted[4] ?? false}
    />,
    <Step5LienWaivers
      key="step5"
      prefill={effectivePrefill}
      lienWaiverMinAmount={lienWaiverMinAmount}
      onLienWaiverMinAmountChange={setLienWaiverMinAmount}
      lienWaiverDescription={lienWaiverDescription}
      onLienWaiverDescriptionChange={setLienWaiverDescription}
      lienWaiverNote={lienWaiverNote}
      onLienWaiverNoteChange={setLienWaiverNote}
    />,
    <Step6Photos
      key="step6"
      prefill={effectivePrefill}
      initialDocuments={photoDocuments}
      onDocumentsChange={setPhotoDocuments}
    />,
    <Step7InvoiceRecommendation
      key="step7"
      prefill={effectivePrefill}
      invoiceAmount={invoiceAmount}
      invoiceDate={invoiceDate}
      invoiceNumber={invoiceNumber}
      servicesRequested={servicesRequested}
      recommendation={recommendation}
      onRecommendationChange={setRecommendation}
      onRecommendationTextChange={setRecommendationText}
      onSignatureDateChange={setSignatureDate}
      signatureDateValue={signatureDate}
      attachmentsList={attachmentsList}
      onAttachmentsListChange={setAttachmentsList}
      payableTo={payableTo}
      onPayableToChange={setPayableTo}
      mailTo={mailTo}
      onMailToChange={setMailTo}
      submitted={stepSubmitted[7] ?? false}
    />,
    <Step8PreviewPublish
      key="step8"
      onToast={showToast}
      prefill={effectivePrefill}
      invoiceAmount={invoiceAmount}
      invoiceDate={invoiceDate}
      invoiceNumber={invoiceNumber}
      reportDate={reportDate}
      recommendation={recommendation}
      recommendationText={recommendationText}
      signatureDate={signatureDate}
      servicesRequested={servicesRequested}
      drawReviewBullets={drawReviewBullets}
      phaseNotes={phaseNotes}
      flagNarratives={flagNarratives}
      extraFlags={extraFlags}
      scheduleNotes={scheduleNotes}
      changeOrders={changeOrders}
      bankItems={bankItems}
      attachmentsList={attachmentsList}
      payableTo={payableTo}
      mailTo={mailTo}
      deliveryMethod={deliveryMethod}
      onDeliveryMethodChange={setDeliveryMethod}
      lockAfterPublish={lockAfterPublish}
      onLockAfterPublishChange={setLockAfterPublish}
      deliveryCc={deliveryCc}
      onDeliveryCcChange={setDeliveryCc}
    />,
  ];

  if (loading) {
    const FieldGroup = ({ w = 90 }: { w?: number }) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <Skeleton width={w} height={11} />
        <Skeleton width="100%" height={36} borderRadius={6} />
      </div>
    );
    return (
      <div className="craPageWrap">
        {/* Steps bar */}
        <div
          style={{
            background: "#fff",
            borderBottom: "1px solid var(--bd)",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            gap: 4,
            height: 56,
            overflowX: "auto",
          }}
        >
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 4,
                flexShrink: 0,
              }}
            >
              <Skeleton width={120} height={32} borderRadius={20} />
              {i < 7 && <Skeleton width={14} height={2} />}
            </div>
          ))}
        </div>

        <div className="craPage">
          {/* SectionHeader */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 8,
              marginBottom: 20,
            }}
          >
            <Skeleton width={260} height={22} />
            <Skeleton width={420} height={13} />
          </div>

          {/* SourceBanner */}
          <div
            style={{
              background: "var(--bg2)",
              borderRadius: 8,
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: 12,
              marginBottom: 20,
            }}
          >
            <Skeleton width={20} height={20} borderRadius={4} />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 5,
                flex: 1,
              }}
            >
              <Skeleton width={220} height={13} />
              <Skeleton width={360} height={11} />
            </div>
          </div>

          {/* Card: Project Overview — 4x cols2 rows */}
          <div className="craCard" style={{ marginBottom: 20, padding: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 18,
              }}
            >
              <Skeleton width={150} height={14} />
              <Skeleton width={70} height={22} borderRadius={10} />
            </div>
            {[
              ["Bank / Lender", 80],
              ["Project Name", 100],
              ["Description", 90],
              ["General Contractor", 130],
            ].map(([_, w], i) => (
              <div
                key={i}
                className="craFieldRow cols2"
                style={{ marginBottom: 14 }}
              >
                <FieldGroup w={Number(w)} />
                <FieldGroup w={Number(w) + 20} />
              </div>
            ))}
          </div>

          {/* Card: Report & Inspection Details — cols4 + cols3 */}
          <div className="craCard" style={{ marginBottom: 20, padding: 10 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 18,
              }}
            >
              <Skeleton width={210} height={14} />
              <Skeleton width={60} height={22} borderRadius={10} />
            </div>
            <div className="craFieldRow cols4" style={{ marginBottom: 14 }}>
              <FieldGroup w={100} />
              <FieldGroup w={110} />
              <FieldGroup w={90} />
              <FieldGroup w={80} />
            </div>
            <div className="craFieldRow cols3">
              <FieldGroup w={110} />
              <FieldGroup w={115} />
              <FieldGroup w={95} />
            </div>
          </div>

          {/* Card: G702 Summary — two-col aia rows */}
          <div className="craCard" style={{ marginBottom: 20, padding: 10 }}>
            <Skeleton width={280} height={14} style={{ marginBottom: 18 }} />
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 16,
              }}
            >
              {[0, 1].map((col) => (
                <div
                  key={col}
                  style={{ display: "flex", flexDirection: "column", gap: 10 }}
                >
                  {Array.from({ length: col === 0 ? 5 : 7 }).map((_, i) => (
                    <div
                      key={i}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                      }}
                    >
                      <Skeleton width={`${55 + (i % 3) * 8}%`} height={12} />
                      <Skeleton width={70} height={12} />
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Publish bar */}
        <div
          style={{
            background: "#fff",
            borderTop: "1px solid var(--bd)",
            padding: "14px 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Skeleton width={160} height={13} />
          <div style={{ display: "flex", gap: 10 }}>
            <Skeleton width={80} height={36} borderRadius={6} />
            <Skeleton width={110} height={36} borderRadius={6} />
            <Skeleton width={170} height={36} borderRadius={6} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="craPageWrap">
      <StepsBar
        steps={steps}
        currentStep={published ? steps.length : currentStep}
        onStepChange={() => {}}
      />

      <main className="craPage" ref={scrollRef}>
        {published ? (
          <SuccessScreen
            onDownload={handleDownload}
            onViewProject={() => navigate("/", { replace: true })}
            onRestart={handleRestart}
            reportNum={effectivePrefill?.nextReportNumber}
            prefill={effectivePrefill ?? undefined}
            lenderName={effectivePrefill?.stakeholders?.lender?.companyName}
            lendingOfficer={
              effectivePrefill?.stakeholders?.lendingOfficer
                ? `${effectivePrefill.stakeholders.lendingOfficer.firstName ?? ""} ${effectivePrefill.stakeholders.lendingOfficer.lastName ?? ""}`.trim()
                : undefined
            }
            ownerName={effectivePrefill?.stakeholders?.owner?.companyName}
            gcName={
              effectivePrefill?.stakeholders?.gc
                ? effectivePrefill.stakeholders.gc.companyName ||
                  `${effectivePrefill.stakeholders.gc.firstName ?? ""} ${effectivePrefill.stakeholders.gc.lastName ?? ""}`.trim()
                : undefined
            }
            deliveryMethod={deliveryMethod}
            deliveryCc={deliveryCc}
            invoiceNumber={invoiceNumber}
            invoiceAmount={invoiceAmount}
          />
        ) : (
          screens.map((screen, idx) => (
            <div
              key={idx}
              style={{ display: currentStep === idx + 1 ? "block" : "none" }}
            >
              {screen}
            </div>
          ))
        )}
      </main>

      {!published ? (
        <PublishBar
          currentStep={currentStep}
          totalSteps={steps.length}
          currentLabel={stepLabels[currentStep - 1]}
          nextLabel={nextLabels[currentStep - 1]}
          isLast={currentStep === steps.length}
          onBack={handleBack}
          onNext={handleNext}
          onSave={handleSaveDraft}
          onPublish={handlePublish}
          isSaving={isSaving}
          isPublishing={isPublishing}
        />
      ) : null}
    </div>
  );
};

export default ConstructionReportAuthoringPage;
